import React, { useState, useEffect } from 'react';
import { Collapse, Button, Badge, Card } from 'react-bootstrap';
import CopyToClipboardIcon from 'components/CopyToClipboardIcon';
import { format } from 'date-fns';
import KjedeListItem from './KjedeListItem';
import Style from './index.module.css';
import {
  Enhetsgruppering,
  EnhetViewModel,
  KontonrOgFakturaAdresseSjekkResponse,
  LatestGrupperingMedlemskap,
} from 'services/KjederegisteretAdminApi/EnhetApi/types';
import PostnummerRegisterApi, {
  PostnummerRegisterEntry,
} from 'services/KjederegisteretAdminApi/PostnummerRegisterApi';

interface Props {
  enhet: EnhetViewModel;
  setLinkedForm: (form: LinkedForm) => void;
  kontonrOgFakturaAdresseSjekkResponse?: KontonrOgFakturaAdresseSjekkResponse;
}

interface LinkedForm {
  tab: string;
  field: string;
}

const EnhetOverview = (props: Props) => {
  const [expandTyper, setExpandTyper] = useState(false);
  const [expandRegionalKjeder, setExpandRegionalKjeder] = useState<boolean>(false);
  const [expandMarkedKjeder, setExpandMarkedKjeder] = useState<boolean>(false);
  const [expandGrupper, setExpandGrupper] = useState<boolean>(false);
  const [showDatoDetaljer, setShowDatoDetaljer] = useState(false);
  const [showAdresseDetaljer, setShowAdresseDetaljer] = useState(false);
  const [postnummerInfo, setPostnummerInfo] = useState<PostnummerRegisterEntry | undefined>();

  useEffect(() => {
    const postnr = props.enhet.besoksadresse?.postnr;
    if (postnr) {
      PostnummerRegisterApi.getByPostkode(postnr)
        .then(setPostnummerInfo)
        .catch(() => setPostnummerInfo(undefined));
    } else {
      setPostnummerInfo(undefined);
    }
  }, [props.enhet.besoksadresse?.postnr]);

  let fakturering: string[] = [];

  if (props.enhet.firma.kanMottaEfaktura) {
    fakturering.push('Efaktura');
  }
  if (props.enhet.firma.fakturaEpost && props.enhet.firma.fakturaEpost.length > 0) {
    fakturering.push('Epost');
  }
  if (props.enhet.fakturaAdresse) {
    fakturering.push('Post');
  }

  const aktivRegionalkjede = props.enhet.regionalkjedeHistorikk?.find((h) => h.isActive);
  const primaryRegionalkjede =
    !props.enhet.sisteRegionalkjedeMedlemskap?.isActive && aktivRegionalkjede
      ? aktivRegionalkjede
      : undefined;
  const primaryRegionalkjedeKjedeId =
    primaryRegionalkjede?.kjedeId ?? props.enhet.sisteRegionalkjedeMedlemskap?.id;

  const aktivMarkedskjede = props.enhet.markedskjedeHistorikk?.find((h) => h.isActive);
  const primaryMarkedskjede =
    !props.enhet.sisteMarkedskjedeMedlemskap?.isActive && aktivMarkedskjede
      ? aktivMarkedskjede
      : undefined;
  const primaryMarkedskjedeKjedeId =
    primaryMarkedskjede?.kjedeId ?? props.enhet.sisteMarkedskjedeMedlemskap?.id;

  const formatDate = (date: Date) => {
    if (!(date instanceof Date)) {
      return '';
    }
    return format(date, 'dd.MM.yyyy');
  };

  const checkKjedeStatus = (item: {
    isActive: boolean;
    isFuture: boolean;
    isHistoric: boolean;
  }) => {
    if (item?.isActive) {
      return (
        <Badge pill bg="success">
          Aktiv
        </Badge>
      );
    } else if (item?.isFuture) {
      return (
        <Badge pill bg="info" text="dark">
          Fremtidig
        </Badge>
      );
    } else if (item?.isHistoric) {
      return (
        <Badge pill bg="warning">
          Nedlagt
        </Badge>
      );
    }
  };

  const checkGrupperingStatus = (item: Enhetsgruppering | LatestGrupperingMedlemskap) => {
    if (
      new Date() >= new Date(item.fraOgMedDato) &&
      (!item.tilOgMedDato || new Date() < new Date(item.tilOgMedDato))
    ) {
      return (
        <Badge pill bg="success">
          Aktiv
        </Badge>
      );
    }

    if (new Date() < new Date(item?.fraOgMedDato) && !item?.tilOgMedDato) {
      return (
        <Badge pill bg="info" text="dark">
          Fremtidig
        </Badge>
      );
    }

    if (new Date(item.tilOgMedDato)) {
      return (
        <Badge pill bg="warning">
          Nedlagt
        </Badge>
      );
    }
  };

  const fieldClass = (hasValue: boolean) =>
    hasValue ? Style.field : `${Style.field} ${Style.fieldMissing}`;

  const checkGlnStatus = (nedlagtDato?: Date) => {
    if (nedlagtDato && new Date() > nedlagtDato) {
      return (
        <Badge pill bg="warning">
          Nedlagt
        </Badge>
      );
    } else {
      return (
        <Badge pill bg="success">
          Aktiv
        </Badge>
      );
    }
  };

  return (
    <div className={Style.container}>
      {/* Juridisk info og datoer */}
      <Card className={Style.sectionCard}>
        <Card.Header className={Style.sectionHeader}>Juridisk info og datoer</Card.Header>
        <Card.Body className={Style.sectionBody}>
          <div className={fieldClass(!!props.enhet.firma?.juridisknavn)}>
            <div className={Style.fieldLabel}>Juridisk navn</div>
            <div className={Style.fieldValue}>
              <span>{props.enhet.firma?.juridisknavn}</span>
              <span className={Style.fieldActions}>
                {!props.enhet.firma?.juridisknavn && (
                  <Button
                    size="sm"
                    variant="link"
                    className={Style.addButton}
                    onClick={() => props.setLinkedForm({ tab: 'basis', field: 'juridiskinfo' })}
                  >
                    Legg til?
                  </Button>
                )}
                <CopyToClipboardIcon val={props.enhet.firma.juridisknavn} />
              </span>
            </div>
          </div>

          <div className={fieldClass(!!props.enhet.firma?.organisasjonsnr)}>
            <div className={Style.fieldLabel}>Organisasjonsnr</div>
            <div className={Style.fieldValue}>
              <span>{props.enhet.firma?.organisasjonsnr}</span>
              <span className={Style.fieldActions}>
                {!props.enhet.firma?.organisasjonsnr && (
                  <Button
                    size="sm"
                    variant="link"
                    className={Style.addButton}
                    onClick={() => props.setLinkedForm({ tab: 'basis', field: 'juridiskinfo' })}
                  >
                    Legg til?
                  </Button>
                )}
                <CopyToClipboardIcon val={props.enhet.firma.organisasjonsnr} />
              </span>
            </div>
          </div>

          <div className={fieldClass(!!props.enhet.firma?.gln)}>
            <div className={Style.fieldLabel}>GLN</div>
            <div className={Style.fieldValue}>
              {props.enhet.firma?.gln ? (
                <span className={Style.inlineGroup}>
                  <span>{props.enhet.firma.gln}</span>
                  <span>{checkGlnStatus(props.enhet.firma.glnNedlagtDato)}</span>
                  <span>({props.enhet.firma?.eierGLNNavn})</span>
                </span>
              ) : (
                <span />
              )}
              <span className={Style.fieldActions}>
                {!props.enhet.firma?.gln && (
                  <Button
                    size="sm"
                    variant="link"
                    className={Style.addButton}
                    onClick={() => props.setLinkedForm({ tab: 'gln', field: '' })}
                  >
                    Legg til?
                  </Button>
                )}
                <CopyToClipboardIcon val={props.enhet.firma.gln} />
              </span>
            </div>
          </div>

          <div className={fieldClass(!!props.enhet.firma?.juridisknavnBedrift)}>
            <div className={Style.fieldLabel}>Juridisk navn bedrift</div>
            <div className={Style.fieldValue}>
              <span>{props.enhet.firma?.juridisknavnBedrift}</span>
              <span className={Style.fieldActions}>
                {!props.enhet.firma?.juridisknavnBedrift && (
                  <Button
                    size="sm"
                    variant="link"
                    className={Style.addButton}
                    onClick={() =>
                      props.setLinkedForm({ tab: 'basis', field: 'juridiskbedriftinfo' })
                    }
                  >
                    Legg til?
                  </Button>
                )}
                <CopyToClipboardIcon val={props.enhet.firma.juridisknavnBedrift} />
              </span>
            </div>
          </div>

          <div className={fieldClass(!!props.enhet.firma?.bedriftsnr)}>
            <div className={Style.fieldLabel}>Bedriftsnr</div>
            <div className={Style.fieldValue}>
              <span>{props.enhet.firma?.bedriftsnr}</span>
              <span className={Style.fieldActions}>
                {!props.enhet.firma?.bedriftsnr && (
                  <Button
                    size="sm"
                    variant="link"
                    className={Style.addButton}
                    onClick={() =>
                      props.setLinkedForm({ tab: 'basis', field: 'juridiskbedriftinfo' })
                    }
                  >
                    Legg til?
                  </Button>
                )}
                <CopyToClipboardIcon val={props.enhet.firma.bedriftsnr} />
              </span>
            </div>
          </div>
          <div className={fieldClass(!!formatDate(props.enhet.firma.oppstartsdato))}>
            <div className={Style.fieldLabel}>Oppstartdato</div>
            <div className={Style.fieldValue}>
              <span>{formatDate(props.enhet.firma.oppstartsdato)}</span>
              <span className={Style.fieldActions}>
                {!formatDate(props.enhet.firma.oppstartsdato) && (
                  <Button
                    size="sm"
                    variant="link"
                    className={Style.addButton}
                    onClick={() => props.setLinkedForm({ tab: 'basis', field: 'oppstartdato' })}
                  >
                    Legg til?
                  </Button>
                )}
                <CopyToClipboardIcon val={formatDate(props.enhet.firma.oppstartsdato)} />
              </span>
            </div>
          </div>

          <Collapse in={showDatoDetaljer}>
            <div>
              <div className={fieldClass(!!formatDate(props.enhet.firma.internOppstartsdato))}>
                <div className={Style.fieldLabel}>Intern oppstartdato</div>
                <div className={Style.fieldValue}>
                  <span>{formatDate(props.enhet.firma.internOppstartsdato)}</span>
                  <CopyToClipboardIcon val={formatDate(props.enhet.firma.internOppstartsdato)} />
                </div>
              </div>

              <div className={Style.field}>
                <div className={Style.fieldLabel}>Post f.o.m dato</div>
                <div className={Style.fieldValue}>
                  <span>
                    {formatDate(props.enhet.firma.postFraOgMedDato) || 'Reservert mot post'}
                  </span>
                  {props.enhet.firma.postFraOgMedDato && (
                    <CopyToClipboardIcon val={formatDate(props.enhet.firma.postFraOgMedDato)} />
                  )}
                </div>
              </div>
            </div>
          </Collapse>
          <div className={Style.field}>
            <div className={Style.fieldLabel} />
            <div className={Style.fieldValue}>
              <Button
                size="sm"
                variant="link"
                className={Style.addButton}
                onClick={() => setShowDatoDetaljer((prev) => !prev)}
              >
                {showDatoDetaljer ? 'Skjul detaljer' : 'Vis mer'}
              </Button>
            </div>
          </div>

          {props.enhet.firma.nedlagtDato && (
            <div className={`${Style.field} ${Style.fieldDanger}`}>
              <div className={Style.fieldLabel}>Nedlagtdato</div>
              <div className={Style.fieldValue}>
                <span>{formatDate(props.enhet.firma.nedlagtDato)}</span>
                <CopyToClipboardIcon val={formatDate(props.enhet.firma.nedlagtDato)} />
              </div>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Type & klassifisering */}
      <Card className={Style.sectionCard}>
        <Card.Header className={Style.sectionHeader}>Type & klassifisering</Card.Header>
        <Card.Body className={Style.sectionBody}>
          <div className={fieldClass(!!props.enhet.enhetType?.kode)}>
            <div className={Style.fieldLabel}>Type(r)</div>
            <div className={Style.fieldValue}>
              {props.enhet.enhetType?.kode ? (
                <span className={Style.inlineGroup}>
                  <span>{props.enhet.enhetType.kode}</span>
                  {props.enhet.enhetTyper.length > 1 && (
                    <Button
                      size="sm"
                      variant="link"
                      className={Style.addButton}
                      aria-expanded={expandTyper}
                      onClick={() => setExpandTyper(!expandTyper)}
                    >
                      {!expandTyper
                        ? `+${props.enhet.enhetTyper.length - 1}`
                        : `-${props.enhet.enhetTyper.length - 1}`}
                    </Button>
                  )}
                </span>
              ) : (
                <span />
              )}
              <span className={Style.fieldActions}>
                {!props.enhet.enhetType?.kode && (
                  <Button
                    size="sm"
                    variant="link"
                    className={Style.addButton}
                    onClick={() => props.setLinkedForm({ tab: 'basis', field: 'enhettyper' })}
                  >
                    Legg til?
                  </Button>
                )}
                <CopyToClipboardIcon val={props.enhet.enhetType?.kode} />
              </span>
            </div>
            <Collapse in={expandTyper}>
              <div>
                {props.enhet.enhetTyper.map(
                  (type) =>
                    type.kode !== props.enhet.enhetType?.kode && (
                      <div key={type.kode} className={Style.collapseItem}>
                        {type.kode}
                      </div>
                    ),
                )}
              </div>
            </Collapse>
          </div>

          <div className={fieldClass(!!props.enhet.bransje?.name)}>
            <div className={Style.fieldLabel}>Bransje</div>
            <div className={Style.fieldValue}>
              <span>{props.enhet.bransje?.name}</span>
              <span className={Style.fieldActions}>
                {!props.enhet.bransje?.name && (
                  <Button
                    size="sm"
                    variant="link"
                    className={Style.addButton}
                    onClick={() => props.setLinkedForm({ tab: 'basis', field: 'bransje' })}
                  >
                    Legg til?
                  </Button>
                )}
                <CopyToClipboardIcon val={props.enhet.bransje?.name} />
              </span>
            </div>
          </div>

          {props.enhet.segment && (
            <div className={fieldClass(true)}>
              <div className={Style.fieldLabel}>Segment</div>
              <div className={Style.fieldValue}>
                <span>{props.enhet.segment}</span>
                <span className={Style.fieldActions}>
                  <CopyToClipboardIcon val={props.enhet.segment} />
                </span>
              </div>
            </div>
          )}

          <div className={fieldClass(!!props.enhet.grossist?.name)}>
            <div className={Style.fieldLabel}>Grossist</div>
            <div className={Style.fieldValue}>
              <span>{props.enhet.grossist?.name}</span>
              <span className={Style.fieldActions}>
                {!props.enhet.grossist?.name && (
                  <Button
                    size="sm"
                    variant="link"
                    className={Style.addButton}
                    onClick={() => props.setLinkedForm({ tab: 'grossist', field: '' })}
                  >
                    Legg til?
                  </Button>
                )}
                <CopyToClipboardIcon val={props.enhet.grossist?.name} />
              </span>
            </div>
          </div>

          <div className={fieldClass(props.enhet.leverandorKundenummer?.length > 0)}>
            <div className={Style.fieldLabel}>Leverandører</div>
            <div className={Style.fieldValue}>
              <span>
                {props.enhet.leverandorKundenummer?.length > 0
                  ? `${props.enhet.leverandorKundenummer.length} kundenummer`
                  : ''}
              </span>
              <span className={Style.fieldActions}>
                {!(props.enhet.leverandorKundenummer?.length > 0) && (
                  <Button
                    size="sm"
                    variant="link"
                    className={Style.addButton}
                    onClick={() => props.setLinkedForm({ tab: 'leverandorKundenummer', field: '' })}
                  >
                    Legg til?
                  </Button>
                )}
                <CopyToClipboardIcon
                  val={
                    props.enhet.leverandorKundenummer?.length > 0
                      ? `${props.enhet.leverandorKundenummer.length} kundenummer`
                      : undefined
                  }
                />
              </span>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Kjeder & grupper */}
      <Card className={Style.sectionCard}>
        <Card.Header className={Style.sectionHeader}>Kjeder & grupper</Card.Header>
        <Card.Body className={Style.sectionBody}>
          <div className={fieldClass(props.enhet.regionalkjedeHistorikk?.length > 0)}>
            <div className={Style.fieldLabel}>Regionalkjede</div>
            <div className={Style.fieldValue}>
              {props.enhet.regionalkjedeHistorikk?.length > 0 ? (
                <span className={Style.inlineGroup}>
                  <span>
                    {primaryRegionalkjede
                      ? primaryRegionalkjede.nameHierarchy[
                          primaryRegionalkjede.nameHierarchy.length - 1
                        ]
                      : props.enhet.sisteRegionalkjedeMedlemskap?.name}
                    {(primaryRegionalkjede?.kundenrIKjede ||
                      props.enhet.sisteRegionalkjedeMedlemskap?.number) &&
                      ` (avd.nr: ${primaryRegionalkjede?.kundenrIKjede ?? props.enhet.sisteRegionalkjedeMedlemskap?.number})`}
                  </span>
                  {(primaryRegionalkjede || props.enhet.sisteRegionalkjedeMedlemskap) && (
                    <span>
                      {checkKjedeStatus(
                        primaryRegionalkjede ?? props.enhet.sisteRegionalkjedeMedlemskap,
                      )}
                    </span>
                  )}
                  {props.enhet.regionalkjedeHistorikk.length > 1 && (
                    <Button
                      size="sm"
                      variant="link"
                      className={Style.addButton}
                      aria-expanded={expandRegionalKjeder}
                      onClick={() => setExpandRegionalKjeder(!expandRegionalKjeder)}
                    >
                      {!expandRegionalKjeder
                        ? `+${props.enhet.regionalkjedeHistorikk.length - 1}`
                        : `-${props.enhet.regionalkjedeHistorikk.length - 1}`}
                    </Button>
                  )}
                </span>
              ) : (
                <span />
              )}
              <span className={Style.fieldActions}>
                {!(props.enhet.regionalkjedeHistorikk?.length > 0) && (
                  <Button
                    size="sm"
                    variant="link"
                    className={Style.addButton}
                    onClick={() => props.setLinkedForm({ tab: 'kjede', field: '' })}
                  >
                    Legg til?
                  </Button>
                )}
                <CopyToClipboardIcon
                  val={
                    primaryRegionalkjede
                      ? primaryRegionalkjede.nameHierarchy[
                          primaryRegionalkjede.nameHierarchy.length - 1
                        ]
                      : props.enhet.sisteRegionalkjedeMedlemskap?.name
                  }
                />
              </span>
            </div>
            <Collapse in={expandRegionalKjeder}>
              <div>
                {props.enhet.regionalkjedeHistorikk?.map(
                  (hist) =>
                    hist.kjedeId !== primaryRegionalkjedeKjedeId && (
                      <div key={hist.kjedeId} className={Style.collapseItem}>
                        <KjedeListItem item={hist} checkKjedeStatus={checkKjedeStatus} />
                      </div>
                    ),
                )}
              </div>
            </Collapse>
          </div>

          <div className={fieldClass(props.enhet.markedskjedeHistorikk?.length > 0)}>
            <div className={Style.fieldLabel}>Markedskjede</div>
            <div className={Style.fieldValue}>
              {props.enhet.markedskjedeHistorikk?.length > 0 ? (
                <span className={Style.inlineGroup}>
                  <span>
                    {primaryMarkedskjede
                      ? primaryMarkedskjede.nameHierarchy[
                          primaryMarkedskjede.nameHierarchy.length - 1
                        ]
                      : props.enhet.sisteMarkedskjedeMedlemskap?.name}
                    {(primaryMarkedskjede?.kundenrIKjede ||
                      props.enhet.sisteMarkedskjedeMedlemskap?.number) &&
                      ` (avd.nr: ${primaryMarkedskjede?.kundenrIKjede ?? props.enhet.sisteMarkedskjedeMedlemskap?.number})`}
                  </span>
                  {(primaryMarkedskjede || props.enhet.sisteMarkedskjedeMedlemskap) && (
                    <span>
                      {checkKjedeStatus(
                        primaryMarkedskjede ?? props.enhet.sisteMarkedskjedeMedlemskap,
                      )}
                    </span>
                  )}
                  {props.enhet.markedskjedeHistorikk.length > 1 && (
                    <Button
                      size="sm"
                      variant="link"
                      className={Style.addButton}
                      aria-expanded={expandMarkedKjeder}
                      onClick={() => setExpandMarkedKjeder(!expandMarkedKjeder)}
                    >
                      {!expandMarkedKjeder
                        ? `+${props.enhet.markedskjedeHistorikk.length - 1}`
                        : `-${props.enhet.markedskjedeHistorikk.length - 1}`}
                    </Button>
                  )}
                </span>
              ) : (
                <span />
              )}
              <span className={Style.fieldActions}>
                {!(props.enhet.markedskjedeHistorikk?.length > 0) && (
                  <Button
                    size="sm"
                    variant="link"
                    className={Style.addButton}
                    onClick={() => props.setLinkedForm({ tab: 'kjede', field: '' })}
                  >
                    Legg til?
                  </Button>
                )}
                <CopyToClipboardIcon
                  val={
                    primaryMarkedskjede
                      ? primaryMarkedskjede.nameHierarchy[
                          primaryMarkedskjede.nameHierarchy.length - 1
                        ]
                      : props.enhet.sisteMarkedskjedeMedlemskap?.name
                  }
                />
              </span>
            </div>
            <Collapse in={expandMarkedKjeder}>
              <div>
                {props.enhet.markedskjedeHistorikk?.map(
                  (hist) =>
                    hist.kjedeId !== primaryMarkedskjedeKjedeId && (
                      <div key={hist.kjedeId} className={Style.collapseItem}>
                        <KjedeListItem item={hist} checkKjedeStatus={checkKjedeStatus} />
                      </div>
                    ),
                )}
              </div>
            </Collapse>
          </div>

          <div className={fieldClass(props.enhet.grupper?.length > 0)}>
            <div className={Style.fieldLabel}>Grupper</div>
            <div className={Style.fieldValue}>
              {props.enhet.grupper?.length > 0 ? (
                <span className={Style.inlineGroup}>
                  <span>
                    {props.enhet.sisteGrupperingMedlemskap?.name} -{' '}
                    {props.enhet.sisteGrupperingMedlemskap?.enhetgrupperingtypekode}
                  </span>
                  {props.enhet.sisteGrupperingMedlemskap && (
                    <span>{checkGrupperingStatus(props.enhet.sisteGrupperingMedlemskap)}</span>
                  )}
                  {props.enhet.grupper.length > 1 && (
                    <Button
                      size="sm"
                      variant="link"
                      className={Style.addButton}
                      aria-expanded={expandGrupper}
                      onClick={() => setExpandGrupper(!expandGrupper)}
                    >
                      {!expandGrupper
                        ? `+${props.enhet.grupper.length - 1}`
                        : `-${props.enhet.grupper.length - 1}`}
                    </Button>
                  )}
                </span>
              ) : (
                <span />
              )}
              <span className={Style.fieldActions}>
                {!(props.enhet.grupper?.length > 0) && (
                  <Button
                    size="sm"
                    variant="link"
                    className={Style.addButton}
                    onClick={() => props.setLinkedForm({ tab: 'grupper', field: '' })}
                  >
                    Legg til?
                  </Button>
                )}
                <CopyToClipboardIcon val={props.enhet.grupper?.[0]?.enhetsgrupperingnavn} />
              </span>
            </div>
            <Collapse in={expandGrupper}>
              <div>
                {props.enhet.grupper?.map((gruppe) => (
                  <div key={gruppe.enhetgrupperingId} className={Style.collapseItem}>
                    <span>
                      {gruppe.enhetsgrupperingnavn} - {gruppe.enhetgrupperingtypekode}
                    </span>
                    <span>{checkGrupperingStatus(gruppe)}</span>
                  </div>
                ))}
              </div>
            </Collapse>
          </div>
        </Card.Body>
      </Card>

      {/* Adresser */}
      <Card className={Style.sectionCard}>
        <Card.Header className={Style.sectionHeader}>Adresser</Card.Header>
        <Card.Body className={Style.sectionBody}>
          <div className={fieldClass(!!props.enhet.besoksadresse?.formattedForOneLine)}>
            <div className={Style.fieldLabel}>Besøksadresse</div>
            <div className={Style.fieldValue}>
              <span>{props.enhet.besoksadresse?.formattedForOneLine}</span>
              <span className={Style.fieldActions}>
                {!props.enhet.besoksadresse?.formattedForOneLine && (
                  <Button
                    size="sm"
                    variant="link"
                    className={Style.addButton}
                    onClick={() => props.setLinkedForm({ tab: 'adresser', field: '' })}
                  >
                    Legg til?
                  </Button>
                )}
                <CopyToClipboardIcon val={props.enhet.besoksadresse?.formattedForOneLine} />
              </span>
            </div>
          </div>

          <div className={fieldClass(!!props.enhet.leveringsadresse?.formattedForOneLine)}>
            <div className={Style.fieldLabel}>Leveringsadresse</div>
            <div className={Style.fieldValue}>
              <span>{props.enhet.leveringsadresse?.formattedForOneLine}</span>
              <span className={Style.fieldActions}>
                {!props.enhet.leveringsadresse?.formattedForOneLine && (
                  <Button
                    size="sm"
                    variant="link"
                    className={Style.addButton}
                    onClick={() => props.setLinkedForm({ tab: 'adresser', field: '' })}
                  >
                    Legg til?
                  </Button>
                )}
                <CopyToClipboardIcon val={props.enhet.leveringsadresse?.formattedForOneLine} />
              </span>
            </div>
          </div>

          <div className={fieldClass(!!props.enhet.postadresse?.formattedForOneLine)}>
            <div className={Style.fieldLabel}>Postadresse</div>
            <div className={Style.fieldValue}>
              <span>{props.enhet.postadresse?.formattedForOneLine}</span>
              <span className={Style.fieldActions}>
                {!props.enhet.postadresse?.formattedForOneLine && (
                  <Button
                    size="sm"
                    variant="link"
                    className={Style.addButton}
                    onClick={() => props.setLinkedForm({ tab: 'adresser', field: '' })}
                  >
                    Legg til?
                  </Button>
                )}
                <CopyToClipboardIcon val={props.enhet.postadresse?.formattedForOneLine} />
              </span>
            </div>
          </div>

          <div className={fieldClass(!!props.enhet.fakturaAdresse?.formattedForOneLine)}>
            <div className={Style.fieldLabel}>Fakturaadresse</div>
            <div className={Style.fieldValue}>
              <span>{props.enhet.fakturaAdresse?.formattedForOneLine}</span>
              <span className={Style.fieldActions}>
                {!props.enhet.fakturaAdresse?.formattedForOneLine && (
                  <Button
                    size="sm"
                    variant="link"
                    className={Style.addButton}
                    onClick={() => props.setLinkedForm({ tab: 'adresser', field: '' })}
                  >
                    Legg til?
                  </Button>
                )}
                <CopyToClipboardIcon val={props.enhet.fakturaAdresse?.formattedForOneLine} />
              </span>
            </div>
          </div>
          <Collapse in={showAdresseDetaljer}>
            <div>
              <div className={fieldClass(!!postnummerInfo?.kommunenavn)}>
                <div className={Style.fieldLabel}>Kommune</div>
                <div className={Style.fieldValue}>
                  <span>
                    {postnummerInfo
                      ? `${postnummerInfo.kommunenavn} (${postnummerInfo.kommunenummer})`
                      : ''}
                  </span>
                </div>
              </div>
              <div className={fieldClass(!!postnummerInfo?.fylkenavn)}>
                <div className={Style.fieldLabel}>Fylke</div>
                <div className={Style.fieldValue}>
                  <span>
                    {postnummerInfo
                      ? `${postnummerInfo.fylkenavn} (${postnummerInfo.fylkenr})`
                      : ''}
                  </span>
                </div>
              </div>
            </div>
          </Collapse>
          <div className={Style.field}>
            <div className={Style.fieldLabel} />
            <div className={Style.fieldValue}>
              <Button
                size="sm"
                variant="link"
                className={Style.addButton}
                onClick={() => setShowAdresseDetaljer((prev) => !prev)}
              >
                {showAdresseDetaljer ? 'Skjul detaljer' : 'Vis mer'}
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Kontakt */}
      <Card className={Style.sectionCard}>
        <Card.Header className={Style.sectionHeader}>Kontakt</Card.Header>
        <Card.Body className={Style.sectionBody}>
          <div className={fieldClass(!!props.enhet.firma?.epost)}>
            <div className={Style.fieldLabel}>E-post</div>
            <div className={Style.fieldValue}>
              <span>{props.enhet.firma?.epost}</span>
              <span className={Style.fieldActions}>
                {!props.enhet.firma?.epost && (
                  <Button
                    size="sm"
                    variant="link"
                    className={Style.addButton}
                    onClick={() => props.setLinkedForm({ tab: 'basis', field: 'epost' })}
                  >
                    Legg til?
                  </Button>
                )}
                <CopyToClipboardIcon val={props.enhet.firma.epost} />
              </span>
            </div>
          </div>

          <div className={fieldClass(!!props.enhet.firma?.fakturaEpost)}>
            <div className={Style.fieldLabel}>Faktura E-post</div>
            <div className={Style.fieldValue}>
              <span>{props.enhet.firma?.fakturaEpost}</span>
              <span className={Style.fieldActions}>
                {!props.enhet.firma?.fakturaEpost && (
                  <Button
                    size="sm"
                    variant="link"
                    className={Style.addButton}
                    onClick={() => props.setLinkedForm({ tab: 'basis', field: 'fakturaepost' })}
                  >
                    Legg til?
                  </Button>
                )}
                <CopyToClipboardIcon val={props.enhet.firma.fakturaEpost} />
              </span>
            </div>
          </div>

          <div className={fieldClass(!!props.enhet.firma?.telefon)}>
            <div className={Style.fieldLabel}>Telefon</div>
            <div className={Style.fieldValue}>
              <span>{props.enhet.firma?.telefon}</span>
              <span className={Style.fieldActions}>
                {!props.enhet.firma?.telefon && (
                  <Button
                    size="sm"
                    variant="link"
                    className={Style.addButton}
                    onClick={() => props.setLinkedForm({ tab: 'basis', field: 'telefon' })}
                  >
                    Legg til?
                  </Button>
                )}
                <CopyToClipboardIcon val={props.enhet.firma.telefon} />
              </span>
            </div>
          </div>

          <div className={fieldClass(!!props.enhet.firma?.kontonr)}>
            <div className={Style.fieldLabel}>Kontonummer</div>
            <div className={Style.fieldValue}>
              <span>{props.enhet.firma?.kontonr}</span>
              <span className={Style.fieldActions}>
                {!props.enhet.firma?.kontonr && (
                  <Button
                    size="sm"
                    variant="link"
                    className={Style.addButton}
                    onClick={() => props.setLinkedForm({ tab: 'basis', field: 'kontonummer' })}
                  >
                    Legg til?
                  </Button>
                )}
                <CopyToClipboardIcon val={props.enhet.firma.kontonr} />
              </span>
            </div>
          </div>

          <div className={fieldClass(fakturering.length > 0)}>
            <div className={Style.fieldLabel}>Fakturering</div>
            <div className={Style.fieldValue}>
              <span>{fakturering.length > 0 ? fakturering.join(', ') : ''}</span>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Økonomi & annet */}
      <Card className={Style.sectionCard}>
        <Card.Header className={Style.sectionHeader}>Økonomi & annet</Card.Header>
        <Card.Body className={Style.sectionBody}>
          <div className={fieldClass(!!props.enhet.omsetning?.omsetningMillioner)}>
            <div className={Style.fieldLabel}>Omsetning (millioner)</div>
            <div className={Style.fieldValue}>
              <span>{props.enhet.omsetning?.omsetningMillioner}</span>
              <span className={Style.fieldActions}>
                {!props.enhet.omsetning?.omsetningMillioner && (
                  <Button
                    size="sm"
                    variant="link"
                    className={Style.addButton}
                    onClick={() => props.setLinkedForm({ tab: 'basis', field: 'omsetning' })}
                  >
                    Legg til?
                  </Button>
                )}
                <CopyToClipboardIcon val={props.enhet.omsetning?.omsetningMillioner?.toString()} />
              </span>
            </div>
          </div>

          <div className={fieldClass(!!props.enhet.firma?.salgsArealKvadratmeter)}>
            <div className={Style.fieldLabel}>Salgsareal (kvadratmeter)</div>
            <div className={Style.fieldValue}>
              <span>{props.enhet.firma?.salgsArealKvadratmeter}</span>
              <span className={Style.fieldActions}>
                {!props.enhet.firma?.salgsArealKvadratmeter && (
                  <Button
                    size="sm"
                    variant="link"
                    className={Style.addButton}
                    onClick={() => props.setLinkedForm({ tab: 'basis', field: 'salgsareal' })}
                  >
                    Legg til?
                  </Button>
                )}
                <CopyToClipboardIcon val={props.enhet.firma?.salgsArealKvadratmeter?.toString()} />
              </span>
            </div>
          </div>

          <div className={Style.field}>
            <div className={Style.fieldLabel}>Er sesongdrift</div>
            <div className={Style.fieldValue}>
              <span>{props.enhet.firma?.erSesongdrift ? 'JA' : 'NEI'}</span>
            </div>
          </div>

          <div className={Style.field}>
            <div className={Style.fieldLabel}>Er offentlig</div>
            <div className={Style.fieldValue}>
              <span>{props.enhet.firma.erOffentlig ? 'JA' : 'NEI'}</span>
            </div>
          </div>

          <div className={fieldClass(!!props.enhet.firma?.landkode)}>
            <div className={Style.fieldLabel}>Land</div>
            <div className={Style.fieldValue}>
              <span>{props.enhet.firma?.landkode}</span>
              <span className={Style.fieldActions}>
                {!props.enhet.firma?.landkode && (
                  <Button
                    size="sm"
                    variant="link"
                    className={Style.addButton}
                    onClick={() => props.setLinkedForm({ tab: 'basis', field: 'landkode-picker' })}
                  >
                    Legg til?
                  </Button>
                )}
                <CopyToClipboardIcon val={props.enhet.firma?.landkode} />
              </span>
            </div>
          </div>

          <div className={fieldClass(!!props.enhet.firma?.produl)}>
            <div className={Style.fieldLabel}>Produl</div>
            <div className={Style.fieldValue}>
              <span>{props.enhet.firma?.produl}</span>
              <span className={Style.fieldActions}>
                <CopyToClipboardIcon val={props.enhet.firma?.produl?.toString()} />
              </span>
            </div>
          </div>

          <div className={fieldClass(!!props.enhet.firma?.statistikknr)}>
            <div className={Style.fieldLabel}>Statistikknummer</div>
            <div className={Style.fieldValue}>
              <span>{props.enhet.firma.statistikknr}</span>
              <span className={Style.fieldActions}>
                <CopyToClipboardIcon val={props.enhet.firma?.statistikknr?.toString()} />
              </span>
            </div>
          </div>

          {props.enhet.firma.forrigeLopenr && (
            <div className={Style.field}>
              <div className={Style.fieldLabel}>Forrige løpenummer</div>
              <div className={Style.fieldValue}>
                <span>{props.enhet.firma.forrigeLopenr}</span>
                <span className={Style.fieldActions}>
                  <CopyToClipboardIcon val={props.enhet.firma?.forrigeLopenr?.toString()} />
                </span>
              </div>
            </div>
          )}

          {props.enhet.firma.nesteLopenr && (
            <div className={Style.field}>
              <div className={Style.fieldLabel}>Neste løpenummer</div>
              <div className={Style.fieldValue}>
                <span>{props.enhet.firma.nesteLopenr}</span>
                <CopyToClipboardIcon val={props.enhet.firma?.nesteLopenr?.toString()} />
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default EnhetOverview;
